import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardText, Briefcase, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatDate, todayISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function OperatorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const production = useQuery({ queryKey: ["entity", "production"], queryFn: async () => (await api.get("/production")).data }).data || [];
  const jobs = useQuery({ queryKey: ["entity", "jobs"], queryFn: async () => (await api.get("/jobs")).data }).data || [];
  const machines = useQuery({ queryKey: ["entity", "machines"], queryFn: async () => (await api.get("/machines")).data }).data || [];

  const mById = Object.fromEntries(machines.map((m) => [m.id, m]));
  const jById = Object.fromEntries(jobs.map((j) => [j.id, j]));
  const today = todayISO();
  const todays = production.filter((p) => p.date === today);
  const activeJobs = jobs.filter((j) => j.job_status === "Active");

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Hi {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-sm text-slate-500 mt-0.5">Your shop-floor dashboard</p>
      </div>

      <Button onClick={() => navigate("/forms/log-production")} className="w-full h-16 text-lg bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="op-log-work">
        <ClipboardText className="h-6 w-6 mr-2" weight="bold" /> Log Today's Work
      </Button>

      <Card className="border-slate-200 shadow-none">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-emerald-500" weight="fill" />
          <h3 className="font-semibold text-slate-900">My Output Today</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50"><TableRow>
              <TableHead>Job</TableHead><TableHead>Machine</TableHead><TableHead className="text-right">Output</TableHead><TableHead className="text-center">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {todays.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No work logged yet today.</TableCell></TableRow>
              ) : todays.map((p) => (
                <TableRow key={p.id} data-testid={`op-today-${p.code}`}>
                  <TableCell>{jById[p.job_id]?.job_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{mById[p.machine_id]?.code || "—"}</TableCell>
                  <TableCell className="text-right">{p.actual_output || 0}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">{p.approval_status || "Pending"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-blue-500" weight="fill" />
          <h3 className="font-semibold text-slate-900">Active Jobs</h3>
        </div>
        <div className="p-4 space-y-2">
          {activeJobs.length === 0 ? <p className="text-sm text-slate-400">No active jobs.</p> :
            activeJobs.map((j) => (
              <div key={j.id} className="rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`op-job-${j.code}`}>
                <div className="font-medium text-slate-800">{j.job_name}</div>
                <div className="text-xs text-slate-500">{j.code} · {j.product_type || "—"}</div>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}
